// ALL game content: the VALUES live in data/*.json (the source of
// truth the tools in meta/tasks.yaml read and write); this file holds
// the behaviours those values name, and wires both into a registry.
// The engine (world/registry/systems) never imports any of it — it
// only ever sees blueprints, behaviours and decorators by name. Want
// a new weapon or enemy? Add a data entry, no code. Want a new KIND
// of weapon? Add a behaviour here and name it from the data.

import { norm, angleTo, dist2 } from "../engine/math.js";
import { hostile } from "./modes.js";
import { item as itemDef } from "./pickups.js";
import WEAPON_DATA from "../../data/weapons.json" with { type: "json" };
import BLUEPRINTS from "../../data/blueprints.json" with { type: "json" };

// Weapons (data/weapons.json). `behavior` names a fire routine
// registered below; `projectile`/`summon`/`zone` name a blueprint.
// Note the Poptart Creature: "Nyan Cat" is copyrighted, so it's a
// poptart-bodied critter that only vaguely evokes the original.
export const WEAPONS = WEAPON_DATA.weapons;
export const DEFAULT_LOADOUT = WEAPON_DATA.defaultLoadout;

// Merge the server's weapon manifest into the CLIENT's static tables (Set 13). Plugin guns (the grenade,
// and since Set 20 the whole meme arsenal) live only on the server; on welcome the client folds in the
// render metadata (shape/label/grip/grab/charge/infinite/uses) for any weapon it lacks and adopts the
// server's loadout ORDER, so the hotbar can show it and the held-weapon pose is right. grip/grab matter:
// without them a two-handed gun would draw one-handed. The client never fires by this data (the server
// does) — it's purely for drawing. Idempotent per join.
export function mergeServerWeapons(manifest) {
    if (!manifest) return;
    for (const [name, s] of Object.entries(manifest.specs || {})) {
        if (!WEAPONS[name]) WEAPONS[name] = { shape: s.shape, label: s.label, behavior: s.behavior || "", tool: !!s.tool, ammo: { infinite: !!s.infinite }, charge: s.charge || undefined, uses: s.uses || 0, grip: s.grip || "one", grab: s.grab ?? undefined };
    }
    if (Array.isArray(manifest.loadout) && manifest.loadout.length) { DEFAULT_LOADOUT.length = 0; DEFAULT_LOADOUT.push(...manifest.loadout); }
}

// ---------------------------------------------------------------------------
// Helpers used by behaviours (kept tiny; they are the decoupled "how").
// ---------------------------------------------------------------------------
// The nearest thing `team` is allowed to shoot. Hostility rather than a named
// team, so a ceiling-cat summoned in FFA hunts the other players, and the same
// cat in co-op hunts the grunts, with no idea either mode exists.
//
// This deliberately uses the BASE rule, not the room's friendly-fire one (Set 15). Friendly fire
// decides whether damage LANDS, not who anything hunts: a room that lets teammates hurt each other
// shouldn't have the enemy AI start picking fights with its own kind, or a homing shot swerve onto the
// nearest friend. Targeting stays teams-differ.
function nearestHostile(world, x, y, team, maxR = Infinity) {
    let best = null;
    let bestD = maxR * maxR;
    for (const id of world.query("body", "pos", "health")) {
        if (!hostile(team, world.get(id, "body").team)) continue;
        const p = world.get(id, "pos");
        const d = dist2(x, y, p.x, p.y);
        if (d < bestD) {
            bestD = d;
            best = id;
        }
    }
    return best;
}

const teamOf = (world, id) => {
    const b = world.get(id, "body");
    return b ? b.team : null;
};

// Everything a weapon spawns inherits the shooter's team — that's
// what decides who it may hurt (THE HOSTILITY RULE in modes.js). The
// blueprint's own team is only a default for anything spawned without
// an owner.
function launch(world, registry, projName, x, y, dirx, diry, owner, speedMul = 1) {
    const id = registry.spawn(world, projName, { pos: { x, y } });
    const proj = world.get(id, "projectile");
    const vel = world.get(id, "vel");
    vel.x = dirx * proj.speed * speedMul;   // speedMul > 1 for a charged shot (control.js)
    vel.y = diry * proj.speed * speedMul;
    proj.owner = owner;
    const body = world.get(id, "body");
    const team = teamOf(world, owner);
    if (body && team) body.team = team;
    const spr = world.get(id, "sprite");
    if (spr) spr.angle = Math.atan2(diry, dirx);
    return id;
}

// ---------------------------------------------------------------------------
// installContent: wires every blueprint, behaviour and decorator into a
// registry. Returns the data tables the systems consult.
// ---------------------------------------------------------------------------
export function installContent(registry) {
    for (const [name, bp] of Object.entries(BLUEPRINTS)) {
        // What you spawn holding comes from weapons.json's
        // defaultLoadout, not from a second copy in the player
        // blueprint. The HUD labels the slots from defaultLoadout
        // while the simulation fires the blueprint's list, so two
        // copies means the HUD eventually labels the wrong gun — a
        // bug you'd only notice by pressing 6 and getting something
        // else. Layered on rather than mutated: the imported JSON is
        // shared, and installContent runs once per room.
        const data = name !== "player" ? bp : {
            ...bp,
            components: { ...bp.components, loadout: { ...bp.components.loadout, weapons: [...DEFAULT_LOADOUT] } },
        };
        registry.defineBlueprint(name, data);
    }

    // --- fire behaviours (weapons reference these by name) ---
    // `precision` (radians) is a per-bullet aim wobble: each shot leaves the barrel at
    // its own random angle within the cone, so even single shots aren't laser-perfect
    // and a burst sprays. Absent/0 (every weapon by default) fires dead straight.
    const wobble = (weapon, rng) => (weapon.precision ? (rng() - 0.5) * weapon.precision : 0);

    registry.defineBehavior("projectile", (world, shooter, weapon, ctx) => {
        const p = world.get(shooter, "pos");
        const a = angleTo(p.x, p.y, ctx.aim.x, ctx.aim.y) + wobble(weapon, ctx.rng);
        const dx = Math.cos(a), dy = Math.sin(a);
        launch(world, registry, weapon.projectile, p.x + dx * 18, p.y + dy * 18, dx, dy, shooter, ctx.speedMul || 1);
    });

    registry.defineBehavior("spread", (world, shooter, weapon, ctx) => {
        const p = world.get(shooter, "pos");
        const base = angleTo(p.x, p.y, ctx.aim.x, ctx.aim.y);
        const n = weapon.count;
        for (let i = 0; i < n; i++) {
            const a = base + (n > 1 ? (i / (n - 1) - 0.5) * weapon.spread : 0) + wobble(weapon, ctx.rng);
            launch(world, registry, weapon.projectile, p.x + Math.cos(a) * 18, p.y + Math.sin(a) * 18, Math.cos(a), Math.sin(a), shooter);
        }
    });

    registry.defineBehavior("summon", (world, shooter, weapon, ctx) => {
        const p = world.get(shooter, "pos");
        const team = teamOf(world, shooter);
        for (let i = 0; i < (weapon.count || 1); i++) {
            const a = ctx.rng() * Math.PI * 2;
            const id = registry.spawn(world, weapon.summon, { pos: { x: p.x + Math.cos(a) * 34, y: p.y + Math.sin(a) * 34 } });
            // A summon fights for whoever summoned it — so its turret
            // hunts what its owner would, in whatever mode this room
            // is running. It also remembers its owner so the frags its
            // shots earn land on the summoner's scoreboard, not nobody's.
            const body = world.get(id, "body");
            if (body && team) body.team = team;
            const summonAi = world.get(id, "ai");
            if (summonAi) summonAi.owner = shooter;
        }
    });

    registry.defineBehavior("zone", (world, shooter, weapon, ctx) => {
        const id = registry.spawn(world, weapon.zone, { pos: { x: ctx.aim.x, y: ctx.aim.y } });
        // The zone carries its dropper's team and id: team so it
        // burns the right people, id so the frag lands on the right
        // scoreboard.
        const z = world.get(id, "zone");
        if (z) { z.team = teamOf(world, shooter); z.owner = shooter; }
    });

    // `drop` (This Is Fine's ember lob) lives in the meme-weapons plugin, and `build` / `mine` (the
    // trowel + mallet) now live in the tools plugin (Set 20) — see content/plugins/{meme-weapons,tools}.lua.
    // The shared projectile/spread/summon behaviours below stay here: they're content-agnostic launch
    // mechanics the plugin weapons reference by name.

    // --- AI behaviours ---
    registry.defineBehavior("chase", (world, id, dt) => {
        const ai = world.get(id, "ai");
        const p = world.get(id, "pos");
        const vel = world.get(id, "vel");
        const grav = world.get(id, "gravity");
        const target = nearestHostile(world, p.x, p.y, teamOf(world, id));
        const spr = world.get(id, "sprite");

        if (target == null) {
            if (!grav) vel.x = vel.y = 0;
            else vel.x = 0;
            return;
        }
        const tp = world.get(target, "pos");
        const dx = tp.x - p.x;
        const dy = tp.y - p.y;

        if (!grav) {
            // Flying enemy (boss): full 2D pursuit, unchanged.
            const [nx, ny] = norm(dx, dy);
            vel.x = nx * ai.speed;
            vel.y = ny * ai.speed;
            if (spr) spr.angle = Math.atan2(dy, dx);
            return;
        }

        // Grounded enemy: walk horizontally toward the player;
        // gravity owns vel.y. Jump when the player is meaningfully
        // above us and we're standing on something, so enemies can
        // climb the platforms to reach you.
        vel.x = Math.sign(dx) * ai.speed;
        if (spr) spr.flip = dx < 0 ? 1 : 0;
        if (grav.grounded && dy < -70 && Math.abs(dx) < 260) {
            vel.y = -(ai.jump || 780);
            grav.grounded = false;
        }
        if (spr) spr.angle = Math.atan2(dy, dx);
    });
    registry.defineBehavior("boss", registry.behavior("chase")); // same movement, tuned via data

    registry.defineBehavior("turret", (world, id, dt) => {
        const ai = world.get(id, "ai");
        ai.timer -= dt;
        if (ai.timer > 0) return;
        const p = world.get(id, "pos");
        const target = nearestHostile(world, p.x, p.y, teamOf(world, id), ai.range);
        if (target == null) return;
        const tp = world.get(target, "pos");
        const [dx, dy] = norm(tp.x - p.x, tp.y - p.y);
        // A summoned turret credits its owner (the summoner); a wild one just owns
        // its own shots. Either way `launch` derives the team from the owner.
        const owner = ai.owner ?? id;
        launch(world, registry, ai.projectile, p.x + dx * 16, p.y + dy * 16, dx, dy, owner);
        ai.timer = ai.cooldown;
    });

    // (The self-arming "gunner" grunt — hunt a dropped weapon, then fire a REAL weapon at the player —
    // moved to the enemies plugin in Set 21.1: it's enemy content, not a shared movement behaviour like
    // chase/turret. It fires through the weapons plugin's new `fire` service, so it's still a real Shoop.)

    // --- decorators (composable projectile modifiers) ---
    registry.defineDecorator("homing", (world, id) => {
        world.get(id, "projectile").homing = 4.5;
    });
    registry.defineDecorator("rainbow", (world, id) => {
        world.get(id, "sprite").trail = "rainbow";
    });
    registry.defineDecorator("piercing", (world, id) => {
        world.get(id, "projectile").pierce += 3;
    });
    // Roll a random lifetime out of `lifetime.range` ([min, max] seconds) at spawn, using
    // the sim's rng so it's deterministic. Lets a blueprint say "lingers 10–20s" in data
    // instead of a system reaching in to randomise a fixed ttl (the fish uses this).
    registry.defineDecorator("lifespan", (world, id, ctx) => {
        const lt = world.get(id, "lifetime");
        if (lt && Array.isArray(lt.range)) lt.ttl = lt.range[0] + ctx.rng() * (lt.range[1] - lt.range[0]);
    });

    return { WEAPONS, DEFAULT_LOADOUT, nearestHostile };
}
